import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import mediaService from '../../services/mediaService';
import './MediaPickerModal.css';

/**
 * MediaPickerModal Component
 * A modal for selecting images from the media library or uploading new ones
 *
 * Props:
 * - isOpen: boolean - Whether modal is open
 * - onClose: () => void - Close handler
 * - onSelect: (url: string) => void - Called when image is selected
 * - currentUrl: string - Currently selected image URL (for highlighting)
 */
function MediaPickerModal({ isOpen, onClose, onSelect, currentUrl }) {
  const [files, setFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUrl, setSelectedUrl] = useState(currentUrl || '');
  const [fetchError, setFetchError] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
      setSelectedUrl(currentUrl || '');
    }
  }, [isOpen, currentUrl]);

  const fetchMedia = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;

      const response = await mediaService.getMedia(params);
      // Filter to only show images on the client side
      const allFiles = response.data.data || [];
      const imageFiles = allFiles.filter(
        (file) => file.type === 'image' || file.mimeType?.startsWith('image/')
      );
      setFiles(imageFiles);
    } catch (err) {
      console.error('Failed to fetch media:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load media';
      setFetchError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const debounce = setTimeout(() => {
        fetchMedia();
      }, 300);
      return () => clearTimeout(debounce);
    }
  }, [searchTerm, isOpen]);

  const handleSelect = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await mediaService.upload(file, 'hero');
      toast.success('Image uploaded successfully');
      // Refresh media list and select the new image
      await fetchMedia();
      setSelectedUrl(uploaded.url);
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // Handle drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="media-picker-overlay" onClick={handleBackdropClick}>
      <div className="media-picker-modal" role="dialog" aria-modal="true">
        <div className="media-picker-header">
          <h2>Select Image</h2>
          <button
            type="button"
            className="media-picker-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="media-picker-toolbar">
          {/* Upload Zone */}
          <div
            className={`media-picker-upload-zone ${isDragOver ? 'drag-over' : ''} ${isUploading ? 'uploading' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isUploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {isUploading ? (
              <>
                <div className="upload-spinner" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
                <span>Drop image here or click to upload</span>
              </>
            )}
          </div>

          {/* Search */}
          <div className="media-picker-search">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search images..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="media-picker-body">
          {isLoading ? (
            <div className="media-picker-loading">
              <div className="loading-spinner" />
              <span>Loading images...</span>
            </div>
          ) : fetchError ? (
            <div className="media-picker-error">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                />
              </svg>
              <p>Failed to load Media Library</p>
              <span className="media-picker-error-detail">{fetchError}</span>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={fetchMedia}
              >
                Try Again
              </button>
            </div>
          ) : files.length === 0 ? (
            <div className="media-picker-empty">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
              <p>No images found</p>
              <span className="media-picker-empty-hint">Upload an image to get started</span>
            </div>
          ) : (
            <div className="media-picker-grid">
              {files.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className={`media-picker-item ${selectedUrl === file.url ? 'selected' : ''}`}
                  onClick={() => setSelectedUrl(file.url)}
                  onDoubleClick={() => {
                    setSelectedUrl(file.url);
                    onSelect(file.url);
                    onClose();
                  }}
                >
                  <img
                    src={file.thumbnailUrl || file.url}
                    alt={file.alt || file.name}
                    loading="lazy"
                  />
                  {selectedUrl === file.url && (
                    <div className="media-picker-check">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                    </div>
                  )}
                  <span className="media-picker-name">{file.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="media-picker-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSelect}
            disabled={!selectedUrl}
          >
            Select Image
          </button>
        </div>
      </div>
    </div>
  );
}

export default MediaPickerModal;
